import axios, { AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

export const setCookie = (
    name: string, 
    value: string | number, 
    minutes: number,
    path: string = "/", 
    secure?: boolean 
) => {
    const expiryDate = new Date();
    expiryDate.setMinutes(expiryDate.getMinutes() + minutes);

    cookies.set(name, value, {
        path,
        expires: expiryDate,
        secure: process.env.NODE_ENV === 'production' ? secure : false, // Use true in production for HTTPS
    });
};


export const getCookie = (name: string) => {
    return cookies.get(name);
};

export const removeCookie = (name: string) => {
    cookies.remove(name, { path: '/' });
};


const setAccessToken = (accessToken: string, expiresInMinutes: number = 50) => {
    setCookie('access_token', accessToken, expiresInMinutes, "/", process.env.NODE_ENV === 'production' );
    const expiryTime = Math.floor(Date.now() / 1000) + expiresInMinutes * 60;
    setCookie('token_expiry', expiryTime, expiresInMinutes, "/", process.env.NODE_ENV === 'production');
};

const setRefreshToken = (refreshToken: string, expiresInMinutes: number = 365 * 24 * 60) => {
    setCookie('refresh_token', refreshToken, expiresInMinutes, "/", process.env.NODE_ENV === 'production' );
    const expiryTime = Math.floor(Date.now() / 1000) + expiresInMinutes * 60;
    setCookie('refresh_token_expiry', expiryTime, expiresInMinutes, "/", process.env.NODE_ENV === 'production');
};

export const clearTokens = () => {
    removeCookie('access_token');
    removeCookie('refresh_token');
    removeCookie('phone_number');
};


export const setPhoneNumberCookie = (username:string) => {
    removeCookie("phone_number");
    setCookie('phone_number', username,  60 * 365 * 24, "/",  process.env.NODE_ENV === 'production' );
}

export const login = async (username: string, password: string): Promise<void> => {
    try {
        const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/token/`, { username, password });
        setAccessToken(response.data.access);
        setRefreshToken(response.data.refresh);
        setCookie('phone_number', username,  60 * 365 * 24, "/",  process.env.NODE_ENV === 'production' );
    } catch (error) {
        throw error;
    }
};

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null; 
export const refresh = async (): Promise<string | null> => {
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }
    try {
        isRefreshing = true;
        const refreshToken = getCookie("refresh_token");
        const refreshTokenExpiry = getCookie("refresh_token_expiry");
        const currentTime = Math.floor(Date.now() / 1000);
        if (!refreshToken || (refreshTokenExpiry && currentTime >= Number(refreshTokenExpiry))) {
            throw new Error("Refresh token expired or not available");
        }
        refreshPromise = axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/token/refresh/`, { refresh: refreshToken })
            .then((response) => {
                setAccessToken(response.data.access);
                setRefreshToken(response.data.refresh);
                return response.data.access;
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    clearTokens();
                    window.location.href = '/login';
                }
                throw error;
            })
            .finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });
        return refreshPromise;
    } catch (error: any) {
        isRefreshing = false;
        refreshPromise = null;
        throw error;
    }
};


export const sendRequest = async (type: string, endpoint: string, data?: any): Promise<any> => {
    try {
        let accessToken = getCookie("access_token");
        const tokenExpiry = getCookie("token_expiry");
        const currentTime = Math.floor(Date.now() / 1000);
        if (!accessToken || (tokenExpiry && currentTime >= Number(tokenExpiry))) {
            accessToken = await refresh();
            if (accessToken) {
                setAccessToken(accessToken);
            } else {
                throw new Error("Unable to refresh token");
            }
        }

        const config: AxiosRequestConfig = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        };

        return await makeRequest(type, endpoint, data, config);
        
    } catch (error: any) {
        if (error.response?.status === 401) {
            clearTokens();
            window.location.href = '/login';
        }
        throw error;
    }
};


export const checkTokenSendRequest = async (type: string, endpoint: string, data?: any): Promise<any> => {
    try {
        let accessToken = getCookie("access_token");
        const refreshToken = getCookie("refresh_token");
        if (!accessToken && !refreshToken) {
            return sendRequestNoToken(type, endpoint, data);
        }
        if (!accessToken) {
            accessToken = await refresh();
        }
        const config: AxiosRequestConfig = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        };
        return await makeRequest(type, endpoint, data, config);
    } catch (error: any) {
        throw error;
    }
};


export const sendRequestNoToken = async (type: string, endpoint: string, data?: any): Promise<any> => {
    try {
        return await makeRequest(type, endpoint, data);
    } catch (error: any) {
        throw error;
    }
};

const makeRequest = async (type: string, endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<any> => {
    switch (type.toUpperCase()) {
        case "GET":
            return (await axios.get(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, config)).data;
        case "POST":
            return (await axios.post(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, data, config)).data;
        case "UPDATE":
            return (await axios.put(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, data, config)).data;
        case "DELETE":
            return (await axios.delete(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, config)).data;
        default:
            return (await axios.post(`${process.env.NEXT_PUBLIC_API_URL}${endpoint}`, data, config)).data;
    }
};



export const logout = async (): Promise<void> => {
    try {
        const refreshToken = cookies.get('refresh_token');
        if (refreshToken) {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/token/logout/`, { refresh: refreshToken });
        }
    } catch (error) {
        console.error('Гарах үйдэл хийхэд алдаа гарлаа:', error);
    } finally {
        clearTokens();
        window.location.href = '/'
    }
};